import axios from "axios";
import IsLoading from "../../../isLoading";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowBackRounded,
  CheckCircleRounded,
  CancelRounded,
  PersonRounded,
  SchoolRounded,
  EmailRounded,
  PhoneRounded,
  CalendarMonthRounded,
  BadgeRounded,
  MenuBookRounded,
  LocationOnRounded,
  WorkspacePremiumRounded,
  VerifiedRounded,
  WarningAmberRounded,
  EventAvailableRounded,
  ArrowForwardRounded,
  FingerprintRounded,
  DescriptionRounded,
  RefreshRounded,
} from "@mui/icons-material";
import "./admitdetail.css";

export default function AdmitDetails() {
  const redirect = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [data, setData] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");

  async function getApplicants() {
    setLoading(true);
    setActionMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_SCRIPT_HOST}/admitting_applicant/details/${id}`
      );

      setData(response.data.response);
    } catch (err) {
      console.log(err);
      setActionMessage(
        err?.response?.data?.message ||
          "Unable to load this applicant. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  async function admintStudent() {
    if (!data) return;

    const payload = {
      firstName: data?.first_Name,
      surName: data?.sur_Name,
      otherName: data?.other_Name,
      appNo: data?.application_number,
      programme: data?.programme_of_study,
      email_address: data?.email,
    };

    setLoading(true);
    setActionMessage("");

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_SCRIPT_HOST}/admitting_applicant/admit/${id}`,
        payload
      );

      setStatus(response.status);
      setActionMessage(
        response?.data?.message || "Candidate admitted successfully."
      );

      await getApplicants();
    } catch (err) {
      console.log(err);
      setActionMessage(
        err?.response?.data?.message ||
          "Unable to admit this candidate. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  async function rejectStudent() {
    setLoading(true);
    setActionMessage("");

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_SCRIPT_HOST}/admitting_applicant/reject/${id}`
      );

      setStatus(response.status);
      setActionMessage(
        response?.data?.message || "Candidate has been rejected."
      );

      await getApplicants();
    } catch (err) {
      console.log(err);
      setActionMessage(
        err?.response?.data?.message ||
          "Unable to reject this candidate. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getApplicants();
  }, [id]);

  const fullName = useMemo(() => {
    if (!data) return "Applicant";

    return [
      data?.first_Name,
      data?.sur_Name,
      data?.other_Name,
    ]
      .filter(Boolean)
      .join(" ");
  }, [data]);

  const isAdmitted =
    data?.admissions?.status === "admitted" || status === 200;

  const isRejected =
    data?.admissions?.status === "rejected";

  const firstSitting =
    data?.results?.first_sitting?.grade || [];

  const secondSitting =
    data?.results?.second_sitting?.grade || [];

  const totalSubjects =
    firstSitting.length + secondSitting.length;

  const passedSubjects = [...firstSitting, ...secondSitting].filter(
    (item) =>
      item?.grade &&
      !["F9", "F", "U"].includes(
        String(item.grade).toUpperCase()
      )
  ).length;

  const schools = [
    {
      key: "primary_school",
      title: "Primary Education",
      data: data?.eductional_records?.primary_school,
    },
    {
      key: "junior_secondary_school",
      title: "Junior Secondary",
      data: data?.eductional_records?.junior_secondary_school,
    },
    {
      key: "senior_secondary_school",
      title: "Senior Secondary",
      data: data?.eductional_records?.senior_secondary_school,
    },
  ];

  const getInitials = () => {
    const names = fullName.split(" ").filter(Boolean);

    return (
      names
        .slice(0, 2)
        .map((name) => name.charAt(0).toUpperCase())
        .join("") || "AP"
    );
  };

  const formatPhone = (phone) => {
    if (!phone) return "Not provided";

    const value = String(phone);

    if (value.startsWith("0")) return value;

    return `0${value}`;
  };

  const renderValue = (value) => {
    if (
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return "Not provided";
    }

    return value;
  };

  const handleReject = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to reject ${fullName}?`
    );

    if (!confirmed) return;

    await rejectStudent();
  };

  const handleAdmit = async () => {
    const confirmed = window.confirm(
      `Confirm admission for ${fullName}?`
    );

    if (!confirmed) return;

    await admintStudent();
  };

  if (loading && !data) {
    return (
      <section className="admit-page admit-page-loading">
        <IsLoading />
      </section>
    );
  }

  if (!data) {
    return (
      <section className="admit-page">
        <div className="admit-empty-state">
          <div className="admit-empty-icon">
            <WarningAmberRounded />
          </div>

          <h2>Applicant unavailable</h2>

          <p>
            We couldn't retrieve this applicant's application
            details.
          </p>

          <button
            className="admit-secondary-button"
            onClick={getApplicants}
          >
            <RefreshRounded />
            Try again
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="admit-page">
      {loading && (
        <div className="admit-loading-overlay">
          <div className="admit-loading-card">
            <div className="admit-loading-spinner" />
            <span>Updating application...</span>
          </div>
        </div>
      )}

      <div className="admit-shell">
        {/* TOP NAVIGATION */}
        <header className="admit-topbar">
          <div className="admit-topbar-left">
            <button
              className="admit-back-button"
              onClick={() => redirect(-1)}
              aria-label="Go back"
            >
              <ArrowBackRounded />
            </button>

            <div>
              <div className="admit-breadcrumb">
                Admissions
                <ArrowForwardRounded />
                Applicant
              </div>

              <h1>Application Review</h1>
            </div>
          </div>

          <div className="admit-topbar-right">
            <button
              className="admit-refresh-button"
              onClick={getApplicants}
              disabled={loading}
            >
              <RefreshRounded />
              Refresh
            </button>

            <div
              className={`admit-status-pill ${
                isAdmitted
                  ? "is-admitted"
                  : isRejected
                  ? "is-rejected"
                  : "is-pending"
              }`}
            >
              {isAdmitted ? (
                <CheckCircleRounded />
              ) : isRejected ? (
                <CancelRounded />
              ) : (
                <EventAvailableRounded />
              )}

              <span>
                {isAdmitted
                  ? "Admitted"
                  : isRejected
                  ? "Rejected"
                  : "Pending Review"}
              </span>
            </div>
          </div>
        </header>

        {/* HERO */}
        <section className="admit-profile-hero">
          <div className="admit-profile-main">
            <div className="admit-avatar-wrapper">
              {data?.passport?.url ? (
                <img
                  src={data.passport.url}
                  alt={fullName}
                  className="admit-profile-image"
                />
              ) : (
                <div className="admit-profile-avatar">
                  {getInitials()}
                </div>
              )}

              <div className="admit-avatar-verification">
                <VerifiedRounded />
              </div>
            </div>

            <div className="admit-profile-identity">
              <div className="admit-eyebrow">
                APPLICATION PROFILE
              </div>

              <h2>{fullName}</h2>

              <div className="admit-profile-meta">
                <span>
                  <FingerprintRounded />
                  {renderValue(data?.application_number)}
                </span>

                <span>
                  <MenuBookRounded />
                  {renderValue(data?.programme_of_study)}
                </span>
              </div>
            </div>
          </div>

          <div className="admit-hero-side">
            <div className="admit-review-label">
              APPLICATION STATUS
            </div>

            <strong>
              {isAdmitted
                ? "Admission confirmed"
                : isRejected
                ? "Application rejected"
                : "Awaiting decision"}
            </strong>

            <span>
              {isAdmitted
                ? "This candidate has been admitted."
                : isRejected
                ? "This application is no longer active."
                : "Review the application before making a decision."}
            </span>
          </div>
        </section>

        {/* QUICK METRICS */}
        <section className="admit-metrics">
          <div className="admit-metric-card">
            <div className="admit-metric-icon blue">
              <DescriptionRounded />
            </div>

            <div>
              <span>Application</span>
              <strong>
                {renderValue(data?.application_number)}
              </strong>
            </div>
          </div>

          <div className="admit-metric-card">
            <div className="admit-metric-icon purple">
              <SchoolRounded />
            </div>

            <div>
              <span>Programme</span>
              <strong>
                {renderValue(data?.programme_of_study)}
              </strong>
            </div>
          </div>

          <div className="admit-metric-card">
            <div className="admit-metric-icon yellow">
              <WorkspacePremiumRounded />
            </div>

            <div>
              <span>Subjects</span>
              <strong>{totalSubjects}</strong>
            </div>
          </div>

          <div className="admit-metric-card">
            <div className="admit-metric-icon green">
              <CheckCircleRounded />
            </div>

            <div>
              <span>Passing subjects</span>
              <strong>{passedSubjects}</strong>
            </div>
          </div>
        </section>

        {/* CONTENT NAVIGATION */}
        <nav className="admit-section-nav">
          <button
            className={
              activeSection === "overview"
                ? "active"
                : ""
            }
            onClick={() => setActiveSection("overview")}
          >
            <PersonRounded />
            Overview
          </button>

          <button
            className={
              activeSection === "education"
                ? "active"
                : ""
            }
            onClick={() => setActiveSection("education")}
          >
            <SchoolRounded />
            Education
          </button>

          <button
            className={
              activeSection === "results"
                ? "active"
                : ""
            }
            onClick={() => setActiveSection("results")}
          >
            <WorkspacePremiumRounded />
            Results
          </button>
        </nav>

        {/* ALERT */}
        {actionMessage && (
          <div
            className={`admit-action-message ${
              status === 200
                ? "success"
                : "error"
            }`}
          >
            {status === 200 ? (
              <CheckCircleRounded />
            ) : (
              <WarningAmberRounded />
            )}

            <span>{actionMessage}</span>
          </div>
        )}

        {/* OVERVIEW */}
        {activeSection === "overview" && (
          <div className="admit-content-grid">
            <div className="admit-main-column">
              <section className="admit-card">
                <div className="admit-card-heading">
                  <div>
                    <span className="admit-card-kicker">
                      PROFILE
                    </span>
                    <h2>Personal Information</h2>
                  </div>

                  <div className="admit-card-icon">
                    <PersonRounded />
                  </div>
                </div>

                <div className="admit-info-grid">
                  <div className="admit-info-item">
                    <span>
                      <PersonRounded />
                      Full name
                    </span>
                    <strong>{fullName}</strong>
                  </div>

                  <div className="admit-info-item">
                    <span>
                      <BadgeRounded />
                      Gender
                    </span>
                    <strong>
                      {renderValue(data?.gender)}
                    </strong>
                  </div>

                  <div className="admit-info-item">
                    <span>
                      <CalendarMonthRounded />
                      Date of birth
                    </span>
                    <strong>
                      {renderValue(
                        data?.Date_of_birth
                      )}
                    </strong>
                  </div>

                  <div className="admit-info-item">
                    <span>
                      <EmailRounded />
                      Email address
                    </span>
                    <strong className="break-text">
                      {renderValue(data?.email)}
                    </strong>
                  </div>

                  <div className="admit-info-item">
                    <span>
                      <PhoneRounded />
                      Phone number
                    </span>
                    <strong>
                      {formatPhone(
                        data?.phone_number
                      )}
                    </strong>
                  </div>

                  <div className="admit-info-item">
                    <span>
                      <FingerprintRounded />
                      Application number
                    </span>
                    <strong>
                      {renderValue(
                        data?.application_number
                      )}
                    </strong>
                  </div>

                  <div className="admit-info-item full">
                    <span>
                      <MenuBookRounded />
                      Programme of study
                    </span>
                    <strong>
                      {renderValue(
                        data?.programme_of_study
                      )}
                    </strong>
                  </div>
                  <div className="admit-info-item full">
                    <span>
                      <MenuBookRounded />
                      Course of study
                    </span>
                    <strong>
                      {renderValue(
                        data?.course_of_study
                      )}
                    </strong>
                  </div>
                </div>
              </section>

              <section className="admit-card">
                <div className="admit-card-heading">
                  <div>
                    <span className="admit-card-kicker">
                      ACADEMIC HISTORY
                    </span>
                    <h2>Schools Attended</h2>
                  </div>

                  <div className="admit-card-icon">
                    <SchoolRounded />
                  </div>
                </div>

                <div className="admit-school-list">
                  {schools.map((school) => (
                    <div
                      className="admit-school-card"
                      key={school.key}
                    >
                      <div className="admit-school-icon">
                        <SchoolRounded />
                      </div>

                      <div className="admit-school-content">
                        <div className="admit-school-title">
                          <div>
                            <span>
                              {school.title}
                            </span>

                            <h3>
                              {renderValue(
                                school.data
                                  ?.institution_name
                              )}
                            </h3>
                          </div>

                          <div className="admit-school-period">
                            {renderValue(
                              school.data
                                ?.year_started
                            )}
                            {" — "}
                            {renderValue(
                              school.data
                                ?.year_finish
                            )}
                          </div>
                        </div>

                        <div className="admit-school-details">
                          <div>
                            <span>Started</span>
                            <strong>
                              {renderValue(
                                school.data
                                  ?.year_started
                              )}
                            </strong>
                          </div>

                          <div>
                            <span>Completed</span>
                            <strong>
                              {renderValue(
                                school.data
                                  ?.year_finish
                              )}
                            </strong>
                          </div>

                          <div>
                            <span>Certificate</span>
                            <strong>
                              {renderValue(
                                school.data
                                  ?.certficate_obtained
                              )}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <aside className="admit-side-column">
              <section className="admit-card admit-summary-card">
                <div className="admit-card-heading">
                  <div>
                    <span className="admit-card-kicker">
                      APPLICATION
                    </span>
                    <h2>Review Summary</h2>
                  </div>
                </div>

                <div className="admit-summary-status">
                  <div
                    className={`admit-summary-status-icon ${
                      isAdmitted
                        ? "success"
                        : isRejected
                        ? "danger"
                        : "pending"
                    }`}
                  >
                    {isAdmitted ? (
                      <CheckCircleRounded />
                    ) : isRejected ? (
                      <CancelRounded />
                    ) : (
                      <EventAvailableRounded />
                    )}
                  </div>

                  <div>
                    <strong>
                      {isAdmitted
                        ? "Admitted"
                        : isRejected
                        ? "Rejected"
                        : "Pending"}
                    </strong>

                    <span>
                      Admission decision
                    </span>
                  </div>
                </div>

                <div className="admit-summary-list">
                  <div>
                    <span>Application</span>
                    <strong>
                      {renderValue(
                        data?.application_number
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Programme</span>
                    <strong>
                      {renderValue(
                        data?.programme_of_study
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Result sittings</span>
                    <strong>
                      {renderValue(
                        data?.No_of_sitting
                      )}
                    </strong>
                  </div>
                </div>
              </section>

              <section className="admit-card admit-contact-card">
                <div className="admit-card-heading">
                  <div>
                    <span className="admit-card-kicker">
                      CONTACT
                    </span>
                    <h2>Applicant Contact</h2>
                  </div>
                </div>

                <a
                  href={`mailto:${data?.email || ""}`}
                  className="admit-contact-row"
                >
                  <div>
                    <EmailRounded />
                  </div>

                  <span>
                    <small>Email</small>
                    <strong>
                      {renderValue(data?.email)}
                    </strong>
                  </span>
                </a>

                <a
                  href={`tel:${formatPhone(
                    data?.phone_number
                  )}`}
                  className="admit-contact-row"
                >
                  <div>
                    <PhoneRounded />
                  </div>

                  <span>
                    <small>Phone</small>
                    <strong>
                      {formatPhone(
                        data?.phone_number
                      )}
                    </strong>
                  </span>
                </a>
              </section>
            </aside>
          </div>
        )}

        {/* EDUCATION */}
        {activeSection === "education" && (
          <section className="admit-card admit-full-card">
            <div className="admit-card-heading">
              <div>
                <span className="admit-card-kicker">
                  ACADEMIC HISTORY
                </span>
                <h2>Complete Education Record</h2>
              </div>

              <div className="admit-card-icon">
                <SchoolRounded />
              </div>
            </div>

            <div className="admit-school-list">
              {schools.map((school) => (
                <div
                  className="admit-school-card"
                  key={school.key}
                >
                  <div className="admit-school-icon">
                    <SchoolRounded />
                  </div>

                  <div className="admit-school-content">
                    <div className="admit-school-title">
                      <div>
                        <span>{school.title}</span>
                        <h3>
                          {renderValue(
                            school.data
                              ?.institution_name
                          )}
                        </h3>
                      </div>
                    </div>

                    <div className="admit-school-details">
                      <div>
                        <span>Year started</span>
                        <strong>
                          {renderValue(
                            school.data
                              ?.year_started
                          )}
                        </strong>
                      </div>

                      <div>
                        <span>Year finished</span>
                        <strong>
                          {renderValue(
                            school.data
                              ?.year_finish
                          )}
                        </strong>
                      </div>

                      <div>
                        <span>Certificate</span>
                        <strong>
                          {renderValue(
                            school.data
                              ?.certficate_obtained
                          )}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* RESULTS */}
        {activeSection === "results" && (
          <div className="admit-results-layout">
            <section className="admit-card">
              <div className="admit-card-heading">
                <div>
                  <span className="admit-card-kicker">
                    ACADEMIC RESULTS
                  </span>
                  <h2>Examination Results</h2>
                </div>

                <div className="admit-sitting-badge">
                  {renderValue(data?.No_of_sitting)}{" "}
                  {data?.No_of_sitting === "1"
                    ? "Sitting"
                    : "Sittings"}
                </div>
              </div>

              <div className="admit-result-stats">
                <div>
                  <span>Total Subjects</span>
                  <strong>{totalSubjects}</strong>
                </div>

                <div>
                  <span>Passed</span>
                  <strong>{passedSubjects}</strong>
                </div>

                <div>
                  <span>Pass Rate</span>
                  <strong>
                    {totalSubjects
                      ? Math.round(
                          (passedSubjects /
                            totalSubjects) *
                            100
                        )
                      : 0}
                    %
                  </strong>
                </div>
              </div>

              <ResultTable
                title="First Sitting"
                results={firstSitting}
              />

              {String(data?.No_of_sitting) === "2" && (
                <ResultTable
                  title="Second Sitting"
                  results={secondSitting}
                />
              )}
            </section>
          </div>
        )}

        {/* STICKY ACTION BAR */}
        <div className="admit-action-bar">
          <div className="admit-action-context">
            <div className="admit-action-avatar">
              {getInitials()}
            </div>

            <div>
              <strong>{fullName}</strong>
              <span>
                {renderValue(
                  data?.application_number
                )}
              </span>
            </div>
          </div>

          <div className="admit-action-buttons">
            <button
              className="admit-cancel-button"
              onClick={() => redirect(-1)}
            >
              <ArrowBackRounded />
              Back
            </button>

            {!isAdmitted && !isRejected && (
              <>
                <button
                  className="admit-reject-button"
                  onClick={handleReject}
                  disabled={loading}
                >
                  <CancelRounded />
                  Reject Candidate
                </button>

                <button
                  className="admit-approve-button"
                  onClick={handleAdmit}
                  disabled={loading}
                >
                  <CheckCircleRounded />
                  Admit Candidate
                </button>
              </>
            )}

            {isAdmitted && (
              <div className="admit-completed-badge">
                <CheckCircleRounded />
                Admission Completed
              </div>
            )}

            {isRejected && (
              <div className="admit-rejected-badge">
                <CancelRounded />
                Application Rejected
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultTable({ title, results = [] }) {
  return (
    <div className="admit-result-block">
      <div className="admit-result-heading">
        <div>
          <span>RESULT</span>
          <h3>{title}</h3>
        </div>

        <div className="admit-result-count">
          {results.length} Subjects
        </div>
      </div>

      {results.length ? (
        <div className="admit-table-wrapper">
          <table className="admit-results-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Subject</th>
                <th>Grade</th>
              </tr>
            </thead>

            <tbody>
              {results.map((result, index) => {
                const grade = String(
                  result?.grade || ""
                ).toUpperCase();

                const isFail =
                  ["F9", "F", "U"].includes(grade);

                return (
                  <tr key={`${result?.subject}-${index}`}>
                    <td>
                      <span className="admit-table-index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                    </td>

                    <td>
                      <strong>
                        {result?.subject ||
                          "Unknown Subject"}
                      </strong>
                    </td>

                    <td>
                      <span
                        className={`admit-grade ${
                          isFail
                            ? "fail"
                            : "pass"
                        }`}
                      >
                        {grade || "N/A"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admit-no-results">
          <DescriptionRounded />
          <span>No examination results available.</span>
        </div>
      )}
    </div>
  );
}